import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Server, MapPin, ArrowRight, Trash2, Shield } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import RackBuilder from './components/network/RackBuilder';
import { useAuthenticatedFetch } from './hooks/useAuthenticatedFetch';

const NetworkAdminPage = () => {
    const authenticatedFetch = useAuthenticatedFetch();
    const [searchParams] = useSearchParams();
    const [locations, setLocations] = useState([]);
    const [racks, setRacks] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedRack, setSelectedRack] = useState(null);
    const [viewMode, setViewMode] = useState('dashboard');
    const [loading, setLoading] = useState(true);
    const [rackDeviceCounts, setRackDeviceCounts] = useState({});
    const hasPreselectedLocation = useRef(false);

    // Form States
    const [showLocForm, setShowLocForm] = useState(false);
    const [showRackForm, setShowRackForm] = useState(false);
    const [newLocName, setNewLocName] = useState('');
    const [newRackName, setNewRackName] = useState('');

    const fetchLocations = useCallback(async () => {
        try {
            const res = await authenticatedFetch('/api/network/locations');
            const data = await res.json();
            setLocations(Array.isArray(data) ? data : []);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setLocations([]);
        } finally {
            setLoading(false);
        }
    }, [authenticatedFetch]);

    const handleDeleteLocation = async (id) => {
        try {
            await authenticatedFetch(`/api/network/locations/${id}`, { method: 'DELETE' });
            if (selectedLocation?.id === id) {
                setSelectedLocation(null);
                setRacks([]);
            }
            fetchLocations();
        } catch (err) {
            console.error(err);
        }
    };

    const fetchRacks = useCallback(async (locId) => {
        try {
            const res = await authenticatedFetch(`/api/network/racks?locationId=${locId}`);
            const data = await res.json();
            setRacks(data);
            // Fetch device counts per rack
            const counts = {};
            for (const rack of data) {
                try {
                    const devRes = await authenticatedFetch(`/api/network/devices?rackId=${rack.id}`);
                    const devData = await devRes.json();
                    counts[rack.id] = Array.isArray(devData) ? devData.length : 0;
                } catch { counts[rack.id] = 0; }
            }
            setRackDeviceCounts(counts);
        } catch (err) {
            console.error(err);
        }
    }, [authenticatedFetch]);

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    useEffect(() => {
        if (hasPreselectedLocation.current || selectedLocation || locations.length === 0) {
            return;
        }

        const locationIdParam = searchParams.get('locationId');
        if (!locationIdParam) {
            return;
        }

        const locationId = Number(locationIdParam);
        if (!Number.isFinite(locationId)) {
            return;
        }

        const foundLocation = locations.find((location) => Number(location.id) === locationId);
        if (foundLocation) {
            hasPreselectedLocation.current = true;
            setSelectedLocation(foundLocation);
        }
    }, [locations, searchParams, selectedLocation]);

    useEffect(() => {
        if (selectedLocation) {
            fetchRacks(selectedLocation.id);
        } else {
            setRacks([]);
        }
    }, [fetchRacks, selectedLocation]);

    const handleSyncMonday = async () => {
        const originalText = document.getElementById('sync-btn-text')?.innerText;
        const btn = document.getElementById('sync-btn');
        if (btn) btn.disabled = true;
        if (document.getElementById('sync-btn-text')) document.getElementById('sync-btn-text').innerText = 'Syncing...';

        try {
            const res = await authenticatedFetch('/api/network/locations/sync-monday', { method: 'POST' });
            const data = await res.json();
            if (res.ok) {
                alert(`Sync Complete!\nFound: ${data.stats.found}\nAdded: ${data.stats.added}\nUpdated: ${data.stats.updated}`);
                fetchLocations();
            } else {
                alert('Sync Failed: ' + (data.detail || data.error || 'Unknown error'));
            }
        } catch (err) {
            console.error(err);
            alert('Sync incorrect or server error.');
        } finally {
            if (btn) btn.disabled = false;
            if (document.getElementById('sync-btn-text')) document.getElementById('sync-btn-text').innerText = originalText || 'Sync from Monday';
        }
    };

    const handleCreateLocation = async (e) => {
        e.preventDefault();
        try {
            await authenticatedFetch('/api/network/locations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newLocName, address: '', coordinates: { lat: 0, lng: 0 } })
            });
            setShowLocForm(false);
            setNewLocName('');
            fetchLocations();
        } catch (err) {
            console.error(err);
        }
    };

    const handleCreateRack = async (e) => {
        e.preventDefault();
        try {
            await authenticatedFetch('/api/network/racks', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ locationId: selectedLocation.id, name: newRackName, maxU: 42 })
            });
            setShowRackForm(false);
            setNewRackName('');
            fetchRacks(selectedLocation.id);
        } catch (err) {
            console.error(err);
        }
    };

    const inputClass = "flex-1 bg-[#0a0a0a] border border-white/10 text-white/80 text-sm px-3 py-2.5 rounded-lg focus:border-white/20 focus:outline-none transition-colors";

    // --- RENDER BUILDER MODE ---
    if (viewMode === 'builder' && selectedRack) {
        return (
            <RackBuilder
                rack={selectedRack}
                authenticatedFetch={authenticatedFetch}
                onBack={() => {
                    setViewMode('dashboard');
                    setSelectedRack(null);
                }}
            />
        );
    }

    return (
        <div className="flex flex-col h-full text-white overflow-y-auto relative" style={{ background: 'linear-gradient(180deg, #050505 0%, #080808 30%, #0a0a0a 60%, #0d0d0d 100%)' }}>

            {/* Ambient glow effects */}
            <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-[0.015] pointer-events-none" style={{ background: 'radial-gradient(circle, #a78bfa 0%, transparent 70%)' }} />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full opacity-[0.015] pointer-events-none" style={{ background: 'radial-gradient(circle, #60a5fa 0%, transparent 70%)' }} />

            <div className="relative z-10 p-8">
                {/* Header */}
                <div className="flex justify-between items-end mb-10">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 rounded-xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center">
                                <Shield size={14} className="text-white/20" />
                            </div>
                            <span className="text-[10px] text-white/10 uppercase tracking-[0.4em] font-medium">Infrastructure Management</span>
                        </div>
                        <h1 className="text-3xl font-bold text-white tracking-tight">Network Architecture</h1>
                    </div>

                    {/* Quick Stats */}
                    <div className="flex gap-6">
                        <div className="text-right">
                            <div className="text-2xl font-bold text-white/80 tabular-nums">{locations.length}</div>
                            <div className="text-[9px] text-white/15 uppercase tracking-[0.3em]">Locations</div>
                        </div>
                        <div className="w-px bg-white/[0.04]" />
                        <div className="text-right">
                            <div className="text-2xl font-bold text-white/80 tabular-nums">{racks.length}</div>
                            <div className="text-[9px] text-white/15 uppercase tracking-[0.3em]">Racks</div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col md:grid md:grid-cols-12 gap-6 flex-1">

                    {/* LEFT COLUMN: LOCATIONS */}
                    <div className="md:col-span-4 lg:col-span-3">
                        <div className="rounded-2xl border border-white/[0.04] overflow-hidden" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.005) 100%)' }}>
                            {/* Section Header */}
                            <div className="px-6 py-5 border-b border-white/[0.04] flex justify-between items-center">
                                <div className="flex items-center gap-2.5">
                                    <MapPin size={14} className="text-emerald-400/40" />
                                    <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">Locations</span>
                                </div>
                                <div className="flex gap-1.5">
                                    <button
                                        id="sync-btn"
                                        onClick={handleSyncMonday}
                                        className="px-3 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-widest text-white/20 hover:text-white/40 bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] hover:border-white/[0.08] transition-all"
                                    >
                                        <span id="sync-btn-text">Sync</span>
                                    </button>
                                    <button
                                        onClick={() => setShowLocForm(!showLocForm)}
                                        className="p-1.5 rounded-lg text-white/15 hover:text-emerald-400/60 bg-white/[0.02] hover:bg-emerald-500/5 border border-white/[0.04] hover:border-emerald-500/15 transition-all"
                                    >
                                        <Plus size={13} />
                                    </button>
                                </div>
                            </div>

                            {/* Create Location Form */}
                            <AnimatePresence>
                                {showLocForm && (
                                    <motion.form
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        onSubmit={handleCreateLocation}
                                        className="overflow-hidden border-b border-white/[0.04]"
                                    >
                                        <div className="flex gap-2 px-6 py-4">
                                            <input
                                                type="text"
                                                value={newLocName}
                                                onChange={(e) => setNewLocName(e.target.value)}
                                                placeholder="Enter Site Name..."
                                                className={inputClass}
                                                autoFocus
                                            />
                                            <button type="submit" className="bg-emerald-500/10 text-emerald-400/70 border border-emerald-500/20 px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider hover:bg-emerald-500/20 hover:text-emerald-400 transition-all">Save</button>
                                        </div>
                                    </motion.form>
                                )}
                            </AnimatePresence>

                            {/* Locations List */}
                            <div className="p-3">
                                {loading && <div className="text-white/10 text-xs font-mono animate-pulse tracking-widest p-4 text-center">Loading...</div>}
                                {!loading && locations.length === 0 && (
                                    <div className="text-center py-12 text-white/8">
                                        <MapPin size={32} className="mx-auto mb-3 opacity-30" />
                                        <p className="text-[10px] uppercase tracking-widest">No locations yet</p>
                                    </div>
                                )}
                                {!loading && locations.map((loc, idx) => {
                                    const isActive = selectedLocation?.id === loc.id;
                                    return (
                                        <motion.div
                                            key={loc.id}
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.03 }}
                                            onClick={() => setSelectedLocation(loc)}
                                            className={`relative px-4 py-3.5 rounded-xl cursor-pointer transition-all group mb-1 ${isActive
                                                ? 'bg-white/[0.05]'
                                                : 'hover:bg-white/[0.02]'
                                                }`}
                                        >
                                            {/* Active indicator bar */}
                                            {isActive && (
                                                <motion.div
                                                    layoutId="activeLocation"
                                                    className="absolute left-0 top-2 bottom-2 w-[2px] rounded-full bg-emerald-400/60"
                                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                                />
                                            )}

                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${isActive ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-white/[0.02] border border-white/[0.04]'}`}>
                                                        <MapPin size={12} className={isActive ? 'text-emerald-400/60' : 'text-white/15'} />
                                                    </div>
                                                    <div>
                                                        <span className={`text-sm font-medium block transition-colors ${isActive ? 'text-white' : 'text-white/35 group-hover:text-white/55'}`}>
                                                            {loc.name}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (window.confirm('Delete this location? All racks and devices will be removed.')) {
                                                                handleDeleteLocation(loc.id);
                                                            }
                                                        }}
                                                        className="p-1.5 text-white/0 group-hover:text-white/10 hover:!text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                    {isActive && <ArrowRight size={12} className="text-white/15" />}
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: RACKS */}
                    <div className="md:col-span-8 lg:col-span-9">
                        <div className="rounded-2xl border border-white/[0.04] min-h-[500px] flex flex-col" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.005) 100%)' }}>
                            {selectedLocation ? (
                                <>
                                    {/* Section Header */}
                                    <div className="px-6 py-5 border-b border-white/[0.04] flex justify-between items-center">
                                        <div className="flex items-center gap-3">
                                            <Server size={14} className="text-blue-400/40" />
                                            <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">Racks</span>
                                            <span className="text-[9px] text-white/15 px-2 py-0.5 rounded-full bg-white/[0.03] border border-white/[0.04]">{selectedLocation.name}</span>
                                        </div>
                                        <button
                                            onClick={() => setShowRackForm(!showRackForm)}
                                            className="px-4 py-2 rounded-lg text-[9px] font-bold uppercase tracking-widest text-white/20 hover:text-white/40 bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] hover:border-white/[0.08] transition-all flex items-center gap-2"
                                        >
                                            <Plus size={11} /> New Rack
                                        </button>
                                    </div>

                                    {/* Create Rack Form */}
                                    <AnimatePresence>
                                        {showRackForm && (
                                            <motion.form
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                onSubmit={handleCreateRack}
                                                className="overflow-hidden border-b border-white/[0.04]"
                                            >
                                                <div className="flex gap-2 max-w-md px-6 py-4">
                                                    <input
                                                        type="text"
                                                        value={newRackName}
                                                        onChange={(e) => setNewRackName(e.target.value)}
                                                        placeholder="Rack Name (e.g. Server Room 1)"
                                                        className={inputClass}
                                                        autoFocus
                                                    />
                                                    <button type="submit" className="bg-white/[0.04] text-white/30 border border-white/[0.06] px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider hover:bg-white/[0.08] hover:text-white/50 transition-all">Save</button>
                                                </div>
                                            </motion.form>
                                        )}
                                    </AnimatePresence>

                                    {/* Racks Grid */}
                                    <div className="p-4 md:p-6 flex-1">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                            {racks.length === 0 && (
                                                <div className="col-span-3 text-center py-20 text-white/8">
                                                    <Server size={36} className="mx-auto mb-3 opacity-30" />
                                                    <p className="text-[10px] uppercase tracking-widest text-white/10">No racks configured</p>
                                                    <p className="text-[10px] text-white/6 mt-1">Create one to start building</p>
                                                </div>
                                            )}
                                            {racks.map((rack, idx) => {
                                                const deviceCount = rackDeviceCounts[rack.id] || 0;
                                                return (
                                                    <motion.div
                                                        key={rack.id}
                                                        initial={{ opacity: 0, y: 12 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: idx * 0.05 }}
                                                        onClick={() => {
                                                            setSelectedRack(rack);
                                                            setViewMode('builder');
                                                        }}
                                                        className="group cursor-pointer relative"
                                                    >
                                                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                                        <div className="relative rounded-2xl border border-white/[0.04] group-hover:border-white/[0.10] p-6 transition-all duration-300 overflow-hidden" style={{ background: 'rgba(255,255,255,0.01)' }}>
                                                            {/* Mini rack visualization */}
                                                            <div className="flex gap-[2px] mb-5 h-16">
                                                                {Array.from({ length: 8 }).map((_, i) => (
                                                                    <div
                                                                        key={i}
                                                                        className={`flex-1 rounded-sm transition-all duration-500 ${i < deviceCount ? 'bg-white/[0.08] group-hover:bg-white/[0.12]' : 'bg-white/[0.02]'}`}
                                                                    />
                                                                ))}
                                                            </div>

                                                            <div className="flex items-start justify-between mb-4">
                                                                <div>
                                                                    <h3 className="text-sm font-semibold text-white/60 group-hover:text-white transition-colors duration-200">{rack.name}</h3>
                                                                    <p className="text-[9px] text-white/12 uppercase tracking-[0.3em] mt-1">{rack.max_u}U Cabinet</p>
                                                                </div>
                                                                <div className="w-8 h-8 rounded-lg bg-white/[0.02] border border-white/[0.04] group-hover:border-white/[0.08] flex items-center justify-center transition-all">
                                                                    <Server size={12} className="text-white/10 group-hover:text-white/30 transition-colors" />
                                                                </div>
                                                            </div>

                                                            {/* Stats row */}
                                                            <div className="flex items-center gap-4 pt-4 border-t border-white/[0.03]">
                                                                <div>
                                                                    <div className="text-lg font-bold text-white/60 tabular-nums">{deviceCount}</div>
                                                                    <div className="text-[8px] text-white/12 uppercase tracking-[0.3em]">Devices</div>
                                                                </div>
                                                                <div className="flex-1" />
                                                                <div className="text-[9px] text-white/15 group-hover:text-white/30 uppercase tracking-widest flex items-center gap-1.5 transition-colors">
                                                                    Open <ArrowRight size={10} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="h-full min-h-[500px] flex flex-col items-center justify-center">
                                    <div className="w-16 h-16 rounded-2xl bg-white/[0.02] border border-white/[0.04] flex items-center justify-center mb-5">
                                        <MapPin size={24} className="text-white/8" />
                                    </div>
                                    <p className="text-white/15 text-sm mb-1">Select a location</p>
                                    <p className="text-white/8 text-xs">to manage its infrastructure</p>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default NetworkAdminPage;
